// components/NavBot.jsx
// The "KB Navigator" — a floating help widget. Click the mascot FAB to open a
// chat panel; questions go to POST /api/navbot (Claude Haiku, grounded on the
// app docs) and answers render with clickable in-app deeplinks. Mounted once in
// AppShell so it rides along on every page. Transcript persists for the session.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { tourStore } from './tour/tourStore';
import './NavBot.css';

const FRAME_BASE = '/assets/kb-navigator/frame-';
const FRAME_COUNT = 8;
const MSG_KEY = 'navbot-messages';
const GREET_KEY = 'navbot-greeted';
const GREETINGS = ["I'm here to help!", 'Ask me a question! 🧭', 'Need directions?'];
const STARTERS = [
  "How do I see next week's grid?",
  'Open my PTO',
  'Set my profile niche preferences',
];

let framesPreloaded = false;
function preloadFrames() {
  if (framesPreloaded || typeof Image === 'undefined') return;
  framesPreloaded = true;
  for (let n = 1; n <= FRAME_COUNT; n++) { const im = new Image(); im.src = `${FRAME_BASE}${n}.png`; }
}

// ── FAB-relative anchoring ──
// The mascot FAB can be dragged anywhere (pos = its top-left in viewport coords;
// null = default bottom-right corner). Place a box of size boxW×boxH beside the
// FAB — preferring above it — and clamp so it's always fully on-screen.
const FAB_SIZE = 62, MARGIN = 8, GAP = 12;
function anchorBox(pos, boxW, boxH, vw, vh) {
  const fabLeft = pos ? pos.left : vw - MARGIN - FAB_SIZE;
  const fabTop  = pos ? pos.top  : vh - MARGIN - FAB_SIZE;
  const fabRight = fabLeft + FAB_SIZE, fabBottom = fabTop + FAB_SIZE;
  // vertical: prefer opening above the FAB, else below, else clamp
  let top;
  if (fabTop - GAP - boxH >= MARGIN) top = fabTop - GAP - boxH;
  else if (fabBottom + GAP + boxH <= vh - MARGIN) top = fabBottom + GAP;
  else top = Math.max(MARGIN, vh - MARGIN - boxH);
  // horizontal: right-align to the FAB, then clamp on-screen
  const left = Math.max(MARGIN, Math.min(vw - MARGIN - boxW, fabRight - boxW));
  return { left, top };
}

// ── Lightweight answer renderer: markdown links + bare app paths → <Link>, **bold**, line breaks ──
const TOKEN_RE = /\[([^\]]+)\]\((\/[^)]+)\)|(\/(?:schedule|reassign)[^\s)]+)/g;
function renderBold(text, key) {
  return text.split(/\*\*([^*]+)\*\*/g).map((p, i) =>
    i % 2 === 1 ? <strong key={`${key}b${i}`}>{p}</strong> : <React.Fragment key={`${key}f${i}`}>{p}</React.Fragment>);
}
function renderInline(text, key) {
  const out = []; let last = 0, m, i = 0;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) out.push(...renderBold(text.slice(last, m.index), `${key}t${i++}`));
    const isMd = !!m[1];
    const to = m[2] || m[3];
    const label = m[1] || m[3];
    if (to.includes('<') || to.includes('>')) {
      // Placeholder path (e.g. /schedule/client/<clientId>) — never a clickable
      // (broken) link. Markdown-link label stays prose; a bare path shows as code.
      if (isMd) out.push(...renderBold(label, `${key}p${i++}`));
      else out.push(<code key={`${key}c${i++}`} className="navbot-code">{label}</code>);
    } else {
      out.push(<Link key={`${key}l${i++}`} to={to} className="navbot-link">{label}</Link>);
    }
    last = TOKEN_RE.lastIndex;
  }
  if (last < text.length) out.push(...renderBold(text.slice(last), `${key}t${i++}`));
  return out;
}
function renderAnswer(text) {
  return (text || '').split('\n').map((line, i) => (
    <div key={i} className="navbot-line">{renderInline(line, `l${i}`)}</div>
  ));
}

export default function NavBot({ staffId, staffName, isSuperUser }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [loading, setLoading] = useState(false);
  const [frame, setFrame] = useState(0);
  const [input, setInput] = useState('');
  const [greeting, setGreeting] = useState(null);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(MSG_KEY) || '[]'); } catch { return []; }
  });
  const scrollRef = useRef(null);
  const fabRef = useRef(null);

  // Draggable FAB position (persisted) + hide-to-edge toggle (persisted).
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('navbot-pos') || 'null'); } catch { return null; }
  });
  const [hidden, setHidden] = useState(() => localStorage.getItem('navbot-hidden') === '1');
  const dragRef = useRef({ dragging: false, moved: false, startX: 0, startY: 0, baseLeft: 0, baseTop: 0 });
  const latestPosRef = useRef(null);
  const suppressClickRef = useRef(false);
  useEffect(() => { try { localStorage.setItem('navbot-hidden', hidden ? '1' : '0'); } catch { /* ignore */ } }, [hidden]);

  // Track the viewport so the panel/restore-tab stay on-screen across resizes.
  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Persist transcript for the session.
  useEffect(() => {
    try { sessionStorage.setItem(MSG_KEY, JSON.stringify(messages)); } catch { /* quota — ignore */ }
  }, [messages]);

  // Wiggle the mascot only while hovering or thinking (frames lazy-loaded).
  const animating = hovering || loading;
  useEffect(() => {
    if (!animating) { setFrame(0); return; }
    preloadFrames();
    const id = setInterval(() => setFrame(f => (f + 1) % FRAME_COUNT), 110);
    return () => clearInterval(id);
  }, [animating]);

  // One-time-per-session speech bubble invite.
  useEffect(() => {
    if (sessionStorage.getItem(GREET_KEY) || open) return;
    const show = setTimeout(() => {
      sessionStorage.setItem(GREET_KEY, '1');
      setGreeting(GREETINGS[(messages.length) % GREETINGS.length]);
      const hide = setTimeout(() => setGreeting(null), 8000);
      return () => clearTimeout(hide);
    }, 3500);
    return () => clearTimeout(show);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to newest.
  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  const ask = useCallback(async (question) => {
    const q = (question || '').trim();
    if (!q || loading) return;
    const history = messages.slice();
    setMessages(m => [...m, { role: 'user', text: q }]);
    setInput('');
    setLoading(true);
    try {
      const r = await fetch('/api/navbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q,
          history,
          context: { path: location.pathname + location.search, staffId, staffName, isSuperUser },
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Error ${r.status}`);
      setMessages(m => [...m, { role: 'assistant', text: data.answer || '(no answer)' }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: e.message || 'Something went wrong — try again.' }]);
    } finally {
      setLoading(false);
    }
  }, [loading, messages, location, staffId, staffName, isSuperUser]);

  const openPanel = () => { setOpen(true); setGreeting(null); preloadFrames(); };

  // Drag the FAB to reposition (persisted). A small movement threshold keeps a
  // plain click opening the chat rather than registering as a drag.
  const startDrag = useCallback((e) => {
    if (e.button !== 0) return;
    const rect = fabRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { dragging: true, moved: false, startX: e.clientX, startY: e.clientY, baseLeft: rect.left, baseTop: rect.top };
    const onMove = (ev) => {
      const d = dragRef.current;
      const dx = ev.clientX - d.startX, dy = ev.clientY - d.startY;
      if (!d.moved && Math.hypot(dx, dy) < 4) return;
      d.moved = true;
      const SIZE = 62, M = 6;
      const left = Math.max(M, Math.min(window.innerWidth - SIZE - M, d.baseLeft + dx));
      const top = Math.max(M, Math.min(window.innerHeight - SIZE - M, d.baseTop + dy));
      latestPosRef.current = { left, top };
      setPos({ left, top });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      dragRef.current.dragging = false;
      if (dragRef.current.moved) {
        suppressClickRef.current = true;
        setTimeout(() => { suppressClickRef.current = false; }, 0);
        if (latestPosRef.current) { try { localStorage.setItem('navbot-pos', JSON.stringify(latestPosRef.current)); } catch { /* ignore */ } }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const handleFabClick = useCallback(() => {
    if (suppressClickRef.current) return;   // we just dragged — don't treat as a click
    if (open) setOpen(false); else openPanel();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hidden → render only a thin restore tab on the screen edge. Keep it pinned to
  // the right edge, but at the FAB's last vertical position (where it was closed).
  if (hidden) {
    const TAB_H = 46;
    const tabStyle = pos
      ? { top: Math.max(MARGIN, Math.min(vp.h - TAB_H - MARGIN, pos.top)), bottom: 'auto' }
      : undefined;
    return (
      <button className="navbot-restore-tab" style={tabStyle} onClick={() => setHidden(false)}
        title="Show the KB Navigator" aria-label="Show the KB Navigator">
        <img src={`${FRAME_BASE}1.png`} alt="" />
      </button>
    );
  }

  const rootStyle = pos ? { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' } : undefined;
  // Anchor the panel beside the FAB and clamp on-screen. Pin width to the same
  // value used for clamping (matches the CSS max-width) so the right edge can't
  // overflow on narrow viewports. Height stays CSS-driven (max-height caps it).
  const panelW = Math.min(360, vp.w - 32);
  const panelH = Math.min(500, vp.h - 110);
  const panelStyle = {
    position: 'fixed', right: 'auto', bottom: 'auto', width: panelW,
    ...anchorBox(pos, panelW, panelH, vp.w, vp.h),
  };
  // The greeting opens above the FAB by default; if the FAB is parked hard against
  // the top edge there's no room, so flip it below (caret points up instead). It
  // also extends left from the FAB; if the FAB hugs the left edge, flip it to
  // extend right so the (up-to-200px) bubble can't clip off-screen.
  const greetBelow = pos ? pos.top - GAP - 44 < MARGIN : false;
  const greetRight = pos ? pos.left + FAB_SIZE - 200 < MARGIN : false;

  return (
    <div className="navbot-root" style={rootStyle}>
      {open && (
        <div className="navbot-panel" role="dialog" aria-label="KB Navigator help" style={panelStyle}>
          <div className="navbot-header">
            <img src={`${FRAME_BASE}1.png`} alt="" className="navbot-header-avatar" />
            <div className="navbot-header-title">
              <div className="navbot-header-name">KB Navigator</div>
              <div className="navbot-header-sub">Ask me how to do anything in RPRS</div>
            </div>
            <button
              className="navbot-tour-btn"
              onClick={() => { setOpen(false); tourStore.openMenu(); }}
              aria-label="Take a tour"
              title="Take a tour of the best features"
            >✨</button>
            <button className="navbot-close" onClick={() => setOpen(false)} aria-label="Close" title="Close">×</button>
          </div>

          <div className="navbot-body" ref={scrollRef}>
            {messages.length === 0 && !loading && (
              <div className="navbot-empty">
                <p>Hi! I'm the KB Navigator. Ask me where things are or how to do something — I'll point you right to it.</p>
                <div className="navbot-chips">
                  {STARTERS.map(s => (
                    <button key={s} className="navbot-chip" onClick={() => ask(s)}>{s}</button>
                  ))}
                </div>
                <button
                  className="navbot-tour-cta"
                  onClick={() => { setOpen(false); tourStore.openMenu(); }}
                >✨ Take a tour of RPRS</button>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`navbot-msg navbot-msg--${m.role}`}>
                {m.role === 'assistant' && <img src={`${FRAME_BASE}1.png`} alt="" className="navbot-msg-avatar" />}
                <div className="navbot-bubble">{m.role === 'assistant' ? renderAnswer(m.text) : m.text}</div>
              </div>
            ))}
            {loading && (
              <div className="navbot-msg navbot-msg--assistant">
                <img src={`${FRAME_BASE}1.png`} alt="" className="navbot-msg-avatar" />
                <div className="navbot-bubble navbot-typing"><span></span><span></span><span></span></div>
              </div>
            )}
          </div>

          <form className="navbot-input-row" onSubmit={e => { e.preventDefault(); ask(input); }}>
            <input
              className="navbot-input"
              placeholder="Ask the Navigator…"
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <button className="navbot-send" type="submit" disabled={loading || !input.trim()} aria-label="Send">▶</button>
          </form>
        </div>
      )}

      {greeting && !open && (
        <div className={`navbot-greeting${greetBelow ? ' navbot-greeting--below' : ''}${greetRight ? ' navbot-greeting--right' : ''}`} onClick={openPanel}>{greeting}</div>
      )}

      <div className="navbot-fab-wrap">
        {!open && (
          <button className="navbot-hide-btn" title="Hide" aria-label="Hide Navigator"
            onClick={(e) => { e.stopPropagation(); setHidden(true); }}>×</button>
        )}
        <button
          ref={fabRef}
          className={`navbot-fab${animating ? ' navbot-fab--active' : ''}`}
          onMouseDown={startDrag}
          onClick={handleFabClick}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          aria-label={open ? 'Close Navigator' : 'Open KB Navigator help'}
          title="KB Navigator — drag to move, click for help"
        >
          <img src={`${FRAME_BASE}${animating ? frame + 1 : 1}.png`} alt="KB Navigator" className="navbot-fab-img" draggable={false} />
        </button>
      </div>
    </div>
  );
}
